import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Route, MapPin, Clock, Navigation, Eye, Car, User, Phone, Play, MapPinned, LayoutGrid, List } from 'lucide-react';
import { ArrowLeft } from 'lucide-react';
import PageHeader from '../../components/layout/PageHeader';
import RefreshButton from '../../components/ui/RefreshButton';
import Pagination from '../../components/ui/Pagination';
import Card, { CardContent } from '../../components/ui/Card';
import Badge from '../../components/ui/Badge';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../../components/ui/Table';
import { formatDateTime, formatCurrency, formatName } from '../../lib/utils';
import { journeyAPI, bookingAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { useSettings } from '../../context/SettingsContext';
import CarLoader from '../../components/ui/CarLoader';

// Helper to format location - handle both string and object
const formatLocationDisplay = (location) => {
  if (!location) return 'N/A';

  // If it's already a simple string (no object notation), return it
  if (typeof location === 'string') {
    // Check if it looks like an object was converted to string
    const trimmed = location.trim();

    // Handle "[object Object]"
    if (trimmed === '[object Object]') return 'N/A';

    // Handle stringified JSON objects like "{ city: 'Gadag' }" or '{"city":"Gadag"}'
    if (trimmed.startsWith('{') && trimmed.endsWith('}')) {
      try {
        // Try parsing as JSON
        const parsed = JSON.parse(trimmed);
        if (parsed.city) {
          return parsed.state && parsed.state.trim() ? `${parsed.city}, ${parsed.state}` : parsed.city;
        }
        if (parsed.address) return parsed.address;
      } catch (e) {
        // Maybe it's JS object notation, try to extract city manually
        const cityMatch = trimmed.match(/city:\s*['"]?([^'",}]+)['"]?/);
        if (cityMatch && cityMatch[1]) {
          return cityMatch[1].trim();
        }
      }
      return 'N/A';
    }

    // It's a normal string, return as is
    return location;
  }

  // Handle actual objects
  if (typeof location === 'object' && location !== null) {
    // Direct property access
    if (location.address && typeof location.address === 'string') {
      return location.address;
    }
    if (location.city && typeof location.city === 'string') {
      const city = location.city.trim();
      const state = location.state && typeof location.state === 'string' ? location.state.trim() : '';
      return state ? `${city}, ${state}` : city;
    }
  }

  return 'N/A';
};

const JourneyList = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { getDefaultDateRange } = useSettings();
  const [journeys, setJourneys] = useState([]);
  const [filteredJourneys, setFilteredJourneys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(1);
  const PER_PAGE = 12;
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'

  // Use default date range from settings
  const [dateRange, setDateRange] = useState(getDefaultDateRange());

  const isDriver = user?.role === 'driver';
  const isOwner = user?.role === 'owner';
  const isAdmin = user?.role === 'admin' || user?.role === 'owner';

  // A trip has a separate driver assigned to it
  const journeyHasDriver = (j) =>
    !!(j.driverId || j.driverName || j.assignedDriver || j.bookingId?.driverId);

  // Owner acts as the driver when no separate driver is assigned to the trip
  const isOwnerDriver = (j) => isOwner && !journeyHasDriver(j);

  useEffect(() => {
    fetchJourneys();
  }, [user?.role]);

  const fetchJourneys = async () => {
    try {
      setLoading(true);

      let journeysData = [];

      if (isDriver) {
        // For drivers, first try to get their assigned bookings, then journeys
        try {
          const [assignmentsRes, journeysRes] = await Promise.all([
            bookingAPI.getMyAssignments(),
            journeyAPI.getMyJourneys().catch(() => ({ data: { journeys: [] } }))
          ]);

          const assignments = assignmentsRes.data.bookings || [];
          const driverJourneys = journeysRes.data.journeys || [];

          // Use the helper function defined above
          const formatLocation = formatLocationDisplay;

          // Get booking IDs that already have journeys to avoid duplicates
          const bookingIdsWithJourneys = new Set(
            driverJourneys
              .filter(j => j.bookingId)
              .map(j => typeof j.bookingId === 'object' ? j.bookingId._id : j.bookingId)
          );

          // Combine - only include bookings that don't already have a journey
          journeysData = [
            ...assignments
              .filter(booking => !bookingIdsWithJourneys.has(booking._id))
              .map(booking => ({
                ...booking,
                _id: booking._id,
                fromLocation: { address: formatLocation(booking.pickupLocation) },
                toLocation: { address: formatLocation(booking.dropoffLocation) },
                startTime: booking.startDate,
                endTime: booking.endDate,
                status: booking.status === 'active' ? 'in-progress' : booking.status,
                estimatedDistance: booking.estimatedKm || 0,
                totalCost: booking.totalCost || 0,
                isBooking: true
              })),
            ...driverJourneys
          ];
        } catch (error) {
          console.error('Error fetching driver data:', error);
        }
      } else {
        // For admin/owner, get all journeys
        try {
          const response = await journeyAPI.getAllJourneys();
          journeysData = response.data.journeys || [];
        } catch (error) {
          console.error('Error fetching journeys:', error);
        }
      }

      setJourneys(journeysData);
      setFilteredJourneys(journeysData);
    } catch (error) {
      console.error('Error fetching journeys:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    filterJourneys();
  }, [journeys, searchTerm, statusFilter, dateRange]);

  const filterJourneys = () => {
    let filtered = [...journeys];

    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(journey => {
        const carReg = journey.carId?.registrationNumber?.toLowerCase() || '';
        const carMake = journey.carId?.make?.toLowerCase() || '';
        const carModel = journey.carId?.model?.toLowerCase() || '';
        const driverName = journey.driverId?.name?.toLowerCase() || '';
        const customerName = journey.customerId?.name?.toLowerCase() || '';
        const fromAddress = journey.fromLocation?.address?.toLowerCase() || '';
        const toAddress = journey.toLocation?.address?.toLowerCase() || '';

        return carReg.includes(search) ||
          carMake.includes(search) ||
          carModel.includes(search) ||
          driverName.includes(search) ||
          customerName.includes(search) ||
          fromAddress.includes(search) ||
          toAddress.includes(search);
      });
    }

    if (statusFilter !== 'all') {
      filtered = filtered.filter(journey => journey.status === statusFilter);
    }

    // Date filter
    if (dateRange.start || dateRange.end) {
      filtered = filtered.filter(journey => {
        const journeyDate = new Date(journey.startTime || journey.createdAt);

        // Reset time to start of day for proper comparison
        const journeyDateOnly = new Date(journeyDate.getFullYear(), journeyDate.getMonth(), journeyDate.getDate());

        if (dateRange.start) {
          const startDate = new Date(dateRange.start);
          const startDateOnly = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());
          if (journeyDateOnly < startDateOnly) return false;
        }

        if (dateRange.end) {
          const endDate = new Date(dateRange.end);
          const endDateOnly = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate());
          if (journeyDateOnly > endDateOnly) return false;
        }

        return true;
      });
    }

    setFilteredJourneys(filtered);
  };

  // Reset to the first page whenever filters change.
  useEffect(() => { setPage(1); }, [searchTerm, statusFilter, dateRange]);

  const totalPages = Math.ceil(filteredJourneys.length / PER_PAGE);
  const pagedJourneys = filteredJourneys.slice((page - 1) * PER_PAGE, page * PER_PAGE);

  const getStatusBadge = (status) => {
    const statusConfig = {
      'pending': { variant: 'warning', label: 'Pending' },
      'in-progress': { variant: 'success', label: 'In Progress' },
      'completed': { variant: 'secondary', label: 'Completed' },
      'cancelled': { variant: 'danger', label: 'Cancelled' }
    };

    const config = statusConfig[status] || statusConfig.pending;
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };

  const getProgressPercentage = (journey) => {
    if (journey.actualDistance && journey.estimatedDistance) {
      return Math.min(100, Math.round((journey.actualDistance / journey.estimatedDistance) * 100));
    }
    if (journey.status === 'completed') return 100;
    if (journey.status === 'in-progress') return 50; // Default for in-progress
    return 0;
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title={isDriver ? "My Trips" : "Trips"}
        description={isDriver ? "View your assigned trips and journeys" : "Manage and track all trips"}
        action={
          <div className="flex items-center gap-2">
            <RefreshButton onClick={fetchJourneys} loading={loading} />
            {isAdmin && (
              <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-2 rounded ${viewMode === 'grid' ? 'bg-white shadow text-blue-600' : 'text-gray-500 hover:text-gray-700'}`}
                  title="Grid View"
                >
                  <LayoutGrid className="h-5 w-5" />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-2 rounded ${viewMode === 'list' ? 'bg-white shadow text-blue-600' : 'text-gray-500 hover:text-gray-700'}`}
                  title="List View"
                >
                  <List className="h-5 w-5" />
                </button>
              </div>
            )}
          </div>
        }
      />

      {/* Filters */}
      <Card className="mb-3">
        <CardContent className="p-2">
          <div className="flex flex-wrap items-center gap-2">
            {/* Search */}
            <div className="flex-1 min-w-[200px]">
              <Input
                placeholder="Search car, driver, destination..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="text-xs h-8"
              />
            </div>

            {/* Status */}
            <div className="flex flex-wrap gap-1.5">
              {[
                { value: 'all', label: 'All', color: 'bg-gray-100 text-gray-700 hover:bg-gray-200', activeColor: 'bg-gray-700 text-white' },
                { value: 'pending', label: 'Pending', color: 'bg-yellow-50 text-yellow-700 hover:bg-yellow-100', activeColor: 'bg-yellow-500 text-white' },
                { value: 'in-progress', label: 'In Progress', color: 'bg-blue-50 text-blue-700 hover:bg-blue-100', activeColor: 'bg-blue-500 text-white' },
                { value: 'completed', label: 'Completed', color: 'bg-green-50 text-green-700 hover:bg-green-100', activeColor: 'bg-green-500 text-white' },
                { value: 'cancelled', label: 'Cancelled', color: 'bg-red-50 text-red-700 hover:bg-red-100', activeColor: 'bg-red-500 text-white' },
              ].map((status) => (
                <button
                  key={status.value}
                  onClick={() => setStatusFilter(status.value)}
                  className={`px-2.5 py-1 rounded-full text-xs font-medium transition-all duration-200 ${
                    statusFilter === status.value ? status.activeColor + ' shadow-sm' : status.color
                  }`}
                >
                  {status.label}
                </button>
              ))}
            </div>

            {/* Date Range - Compact */}
            <div className="flex items-center gap-1 bg-gray-50 rounded-lg px-2 py-1">
              <input
                type="date"
                value={dateRange.start}
                onChange={(e) => setDateRange(prev => ({ ...prev, start: e.target.value }))}
                className="text-xs h-6 px-1 border-0 bg-transparent focus:ring-0 w-28"
              />
              <span className="text-gray-400 text-xs">→</span>
              <input
                type="date"
                value={dateRange.end}
                onChange={(e) => setDateRange(prev => ({ ...prev, end: e.target.value }))}
                className="text-xs h-6 px-1 border-0 bg-transparent focus:ring-0 w-28"
              />
              {(dateRange.start || dateRange.end) && (
                <button
                  onClick={() => setDateRange({ start: '', end: '' })}
                  className="text-gray-400 hover:text-red-500 ml-1"
                  title="Clear dates"
                >
                  ×
                </button>
              )}
            </div>

            {/* Reset Button */}
            <button
              onClick={() => setDateRange(getDefaultDateRange())}
              className="text-xs text-gray-500 hover:text-gray-700 px-2 py-1 hover:bg-gray-100 rounded"
            >
              Reset
            </button>
          </div>
        </CardContent>
      </Card>

      {/* Loading State */}
      {loading ? (
        <Card>
          <CardContent className="py-6">
            <CarLoader message="Loading trips..." />
          </CardContent>
        </Card>
      ) : (
        <>
          {/* List View (Table) for Admin/Owner */}
          {viewMode === 'list' && isAdmin ? (
            <Card className="mb-3">
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="text-xs">Car</TableHead>
                        <TableHead className="text-xs">Driver</TableHead>
                        <TableHead className="text-xs">Customer</TableHead>
                        <TableHead className="text-xs">From</TableHead>
                        <TableHead className="text-xs">To</TableHead>
                        <TableHead className="text-xs">Date</TableHead>
                        <TableHead className="text-xs">Distance</TableHead>
                        <TableHead className="text-xs">Cost</TableHead>
                        <TableHead className="text-xs">Status</TableHead>
                        <TableHead className="text-xs">Payment</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {pagedJourneys.map((journey) => (
                        <TableRow
                          key={journey._id}
                          className="hover:bg-gray-50 cursor-pointer"
                          onClick={() => {
                            if (journey.isBooking) {
                              navigate(`/driver/active-trip/booking/${journey._id}`);
                            } else {
                              navigate(`/journeys/${journey._id}`);
                            }
                          }}
                        >
                          <TableCell className="text-xs">
                            <div className="flex items-center gap-2">
                              {journey.carId?.images && journey.carId.images.length > 0 ? (
                                <img
                                  src={journey.carId.images[0]}
                                  alt={`${formatName(journey.carId.make)} ${formatName(journey.carId.model)}`}
                                  className="w-10 h-8 object-cover rounded"
                                />
                              ) : (
                                <div className="w-10 h-8 bg-gray-100 rounded flex items-center justify-center">
                                  <Car className="h-4 w-4 text-gray-400" />
                                </div>
                              )}
                              <div>
                                <p className="font-medium">{formatName(journey.carId?.make)} {formatName(journey.carId?.model)}</p>
                                <p className="text-gray-500">{journey.carId?.registrationNumber}</p>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell className="text-xs">
                            {formatName(journey.driverId?.name || journey.driverName || journey.bookingId?.driverId?.name) || 'N/A'}
                          </TableCell>
                          <TableCell className="text-xs">
                            <div>
                              <p>{journey.customerId?.name || 'N/A'}</p>
                              {journey.customerId?.phone && (
                                <p className="text-gray-500">{journey.customerId.phone}</p>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="text-xs max-w-[120px] truncate" title={formatLocationDisplay(journey.fromLocation?.address || journey.pickupLocation)}>
                            {formatLocationDisplay(journey.fromLocation?.address) !== 'N/A'
                              ? formatLocationDisplay(journey.fromLocation?.address)
                              : formatLocationDisplay(journey.pickupLocation)}
                          </TableCell>
                          <TableCell className="text-xs max-w-[120px] truncate" title={formatLocationDisplay(journey.toLocation?.address || journey.dropoffLocation)}>
                            {formatLocationDisplay(journey.toLocation?.address) !== 'N/A'
                              ? formatLocationDisplay(journey.toLocation?.address)
                              : formatLocationDisplay(journey.dropoffLocation)}
                          </TableCell>
                          <TableCell className="text-xs whitespace-nowrap">
                            {formatDateTime(journey.status === 'completed' ? journey.endTime : journey.startTime)}
                          </TableCell>
                          <TableCell className="text-xs">
                            {journey.actualDistance || journey.estimatedDistance || journey.estimatedKm || 0} km
                          </TableCell>
                          <TableCell className="text-xs font-medium text-blue-600">
                            {formatCurrency(journey.totalCost || 0)}
                          </TableCell>
                          <TableCell>
                            {getStatusBadge(journey.status)}
                          </TableCell>
                          <TableCell>
                            {journey.status === 'completed' ? (
                              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                                journey.paymentStatus === 'paid' ? 'bg-green-100 text-green-700' :
                                journey.paymentStatus === 'partial' ? 'bg-orange-100 text-orange-700' :
                                journey.paymentStatus === 'refunded' ? 'bg-red-100 text-red-700' :
                                'bg-yellow-100 text-yellow-700'
                              }`}>
                                {journey.paymentStatus === 'paid' ? 'Paid' :
                                 journey.paymentStatus === 'partial' ? 'Partial' :
                                 journey.paymentStatus === 'refunded' ? 'Refunded' : 'Pending'}
                              </span>
                            ) : (
                              <span className="text-xs text-gray-400">-</span>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  {filteredJourneys.length === 0 && (
                    <div className="text-center py-8">
                      <Route className="h-10 w-10 text-gray-400 mx-auto mb-2" />
                      <p className="text-xs text-gray-500">No journeys found</p>
                    </div>
                  )}
                </div>
                <Pagination page={page} totalPages={totalPages} onChange={setPage} />
              </CardContent>
            </Card>
          ) : (
          /* Journeys Grid */
          <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 mb-3">
            {pagedJourneys.map((journey) => (
              <Card
                key={journey._id}
                className="hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => {
                  if (journey.isBooking) {
                    navigate(`/driver/active-trip/booking/${journey._id}`);
                  } else {
                    navigate(`/journeys/${journey._id}`);
                  }
                }}
              >
                <CardContent className="p-3">
                  {/* Car Image */}
                  {journey.carId?.images && journey.carId.images.length > 0 && (
                    <div className="mb-3 rounded-lg overflow-hidden">
                      <img
                        src={journey.carId.images[0]}
                        alt={`${formatName(journey.carId.make)} ${formatName(journey.carId.model)}`}
                        className="w-full h-40 object-cover"
                      />
                    </div>
                  )}

                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center">
                      {/* Car placeholder if no image */}
                      {(!journey.carId?.images || journey.carId.images.length === 0) && (
                        <div className="w-10 h-10 bg-gray-100 rounded flex items-center justify-center mr-2">
                          <Car className="h-5 w-5 text-gray-400" />
                        </div>
                      )}
                      <div>
                        <h3 className="text-sm font-bold text-gray-900">
                          {formatName(journey.carId?.make) || 'N/A'} {formatName(journey.carId?.model) || ''}
                        </h3>
                        <p className="text-xs text-gray-500">
                          {journey.carId?.registrationNumber || 'N/A'}
                        </p>
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-0.5">
                      {getStatusBadge(journey.status)}
                      {/* Trip Type Badge */}
                      {journey.rentalType && (
                        <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${
                          journey.rentalType === 'daily' ? 'bg-purple-100 text-purple-700' :
                          journey.rentalType === 'rental' ? 'bg-green-100 text-green-700' :
                          journey.rentalType === 'km-based' ? 'bg-orange-100 text-orange-700' :
                          journey.rentalType === 'outstation' ? 'bg-teal-100 text-teal-700' :
                          journey.rentalType === 'local' ? 'bg-indigo-100 text-indigo-700' :
                          'bg-gray-100 text-gray-700'
                        }`}>
                          {journey.rentalType === 'daily' && '📅 Daily'}
                          {journey.rentalType === 'rental' && '📆 Multi-Day'}
                          {journey.rentalType === 'km-based' && '📊 Per KM'}
                          {journey.rentalType === 'outstation' && '🗺️ Outstation'}
                          {journey.rentalType === 'local' && '📍 Local'}
                          {journey.rentalType === 'per-km' && '📊 Per KM'}
                          {!['daily', 'rental', 'km-based', 'outstation', 'local', 'per-km'].includes(journey.rentalType) && journey.rentalType}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Customer Info */}
                  {journey.customerId && (
                    <div className="flex items-center mb-2 p-1.5 bg-gray-50 rounded">
                      <User className="h-3 w-3 text-gray-500 mr-1.5" />
                      <span className="text-xs font-medium">
                        {formatName(journey.customerId?.name) || 'Customer'}
                      </span>
                      {(journey.customerId?.phone || journey.customerPhone) && (
                        <span className="text-xs text-blue-600 ml-2">
                          {journey.customerId?.phone || journey.customerPhone}
                        </span>
                      )}
                    </div>
                  )}

                  {/* Driver Info */}
                  {(() => {
                    const driverName = journey.driverId?.name || journey.driverName || journey.assignedDriver?.name || journey.bookingId?.driverId?.name;
                    const driverPhone = journey.driverId?.phone || journey.assignedDriver?.phone || journey.bookingId?.driverId?.phone;
                    return driverName ? (
                      <div className="flex items-center mb-2 p-1.5 bg-green-50 rounded">
                        <User className="h-3 w-3 text-green-600 mr-1.5" />
                        <span className="text-xs font-medium text-green-800">
                          {formatName(driverName)}
                        </span>
                        {driverPhone && (
                          <span className="text-xs text-green-600 ml-auto">
                            {driverPhone}
                          </span>
                        )}
                      </div>
                    ) : null;
                  })()}

                  <div className="space-y-2 mb-2">
                    <div className="flex items-start">
                      <MapPin className="h-3.5 w-3.5 text-green-600 mt-0.5 mr-2 flex-shrink-0" />
                      <div>
                        <p className="text-xs text-gray-500">Pickup Location</p>
                        <p className="text-xs font-medium">
                          {formatLocationDisplay(journey.fromLocation?.address) !== 'N/A'
                            ? formatLocationDisplay(journey.fromLocation?.address)
                            : formatLocationDisplay(journey.pickupLocation)}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start">
                      <MapPin className="h-3.5 w-3.5 text-red-600 mt-0.5 mr-2 flex-shrink-0" />
                      <div>
                        <p className="text-xs text-gray-500">Drop Location</p>
                        <p className="text-xs font-medium">
                          {formatLocationDisplay(journey.toLocation?.address) !== 'N/A'
                            ? formatLocationDisplay(journey.toLocation?.address)
                            : formatLocationDisplay(journey.dropoffLocation)}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start">
                      <Clock className="h-3.5 w-3.5 text-gray-600 mt-0.5 mr-2 flex-shrink-0" />
                      <div>
                        <p className="text-xs text-gray-500">
                          {journey.status === 'completed' ? 'Completed On' : 'Start Date'}
                        </p>
                        <p className="text-xs font-medium">
                          {formatDateTime(journey.status === 'completed' ? journey.endTime : journey.startTime)}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Trip Details Info */}
                  <div className={`grid ${journey.status === 'completed' ? 'grid-cols-3' : 'grid-cols-2'} gap-2 mb-2 p-2 bg-gray-50 rounded`}>
                    <div>
                      <p className="text-xs text-gray-500">
                        {journey.rentalType === 'local' ? 'Duration' : 'Distance'}
                      </p>
                      <p className="text-xs font-semibold">
                        {journey.rentalType === 'local'
                          ? `${journey.hours || 4} hours`
                          : `${journey.actualDistance || journey.estimatedDistance || journey.estimatedKm || 0} km`
                        }
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Total Cost</p>
                      <p className="text-xs font-semibold text-blue-600">
                        {formatCurrency(journey.totalCost || 0)}
                      </p>
                    </div>
                    {journey.status === 'completed' && (
                      <div>
                        <p className="text-xs text-gray-500">Payment</p>
                        <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${
                          journey.paymentStatus === 'paid' ? 'bg-green-100 text-green-700' :
                          journey.paymentStatus === 'partial' ? 'bg-orange-100 text-orange-700' :
                          journey.paymentStatus === 'refunded' ? 'bg-red-100 text-red-700' :
                          'bg-yellow-100 text-yellow-700'
                        }`}>
                          {journey.paymentStatus === 'paid' ? 'Paid' :
                           journey.paymentStatus === 'partial' ? 'Partial' :
                           journey.paymentStatus === 'refunded' ? 'Refunded' : 'Pending'}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Progress Bar for in-progress journeys */}
                  {journey.status === 'in-progress' && (
                    <div className="mb-2">
                      <div className="flex justify-between text-xs mb-1">
                        <span className="text-gray-600">In Progress</span>
                        <span className="font-medium text-blue-600">Active</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-1.5">
                        <div
                          className="bg-blue-600 h-1.5 rounded-full transition-all animate-pulse"
                          style={{ width: '60%' }}
                        />
                      </div>
                    </div>
                  )}

                  {/* Action buttons - only for special actions */}
                  {(() => {
                  // Owner with no driver assigned drives the trip themselves
                  const actsAsDriver = isDriver || isOwnerDriver(journey);
                  return (
                  (actsAsDriver && (journey.status === 'confirmed' || journey.status === 'pending' || journey.status === 'scheduled' || journey.status === 'in-progress')) ||
                   (!actsAsDriver && journey.status === 'in-progress' && journey.trackingCode) ? (
                    <div className="pt-2 border-t">
                      {actsAsDriver && (journey.status === 'confirmed' || journey.status === 'pending' || journey.status === 'scheduled') && (
                        <Button
                          size="sm"
                          className="w-full text-xs h-7 bg-green-600 hover:bg-green-700"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (journey.isBooking) {
                              navigate(`/driver/active-trip/booking/${journey._id}`);
                            } else {
                              navigate(`/driver/active-trip/${journey._id}`);
                            }
                          }}
                        >
                          <Play className="h-3 w-3 mr-1" />
                          Start Trip
                        </Button>
                      )}

                      {actsAsDriver && journey.status === 'in-progress' && (
                        <Button
                          size="sm"
                          className="w-full text-xs h-7 bg-blue-600 hover:bg-blue-700"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (journey.isBooking) {
                              navigate(`/driver/active-trip/booking/${journey._id}`);
                            } else {
                              navigate(`/driver/active-trip/${journey._id}`);
                            }
                          }}
                        >
                          <MapPinned className="h-3 w-3 mr-1" />
                          Continue Trip
                        </Button>
                      )}

                      {!actsAsDriver && journey.status === 'in-progress' && journey.trackingCode && (
                        <Button
                          size="sm"
                          className="w-full text-xs h-7 bg-blue-600 hover:bg-blue-700"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/track/${journey.trackingCode}`);
                          }}
                        >
                          <MapPinned className="h-3 w-3 mr-1" />
                          Track Driver
                        </Button>
                      )}
                    </div>
                  ) : null
                  );
                  })()}
                </CardContent>
              </Card>
            ))}
          </div>
          <Pagination page={page} totalPages={totalPages} onChange={setPage} />
          </>
          )}
        </>
      )}

      {!loading && filteredJourneys.length === 0 && viewMode === 'grid' && (
        <Card>
          <CardContent className="text-center py-6">
            <Route className="h-10 w-10 text-gray-400 mx-auto mb-2" />
            <p className="text-xs text-gray-500">
              {isDriver
                ? 'No trips assigned yet. Trips will appear here when admin assigns them to you.'
                : 'No journeys found'}
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default JourneyList;
